import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Container, Row, Col, Card, Spinner, Pagination } from 'react-bootstrap';
import { useLanguage } from '../contexts/LanguageContext';
import { t } from '../utils/i18n';
import SiteHeader from './SiteHeader';
import { setPageOgMeta } from '../utils/ogMeta';
import './Landing.css';

const API_BASE = process.env.REACT_APP_API_URL || '';
const PAGE_SIZE = 24;

function getPostUrl(post) {
  if (!post?.slug) return '#';
  const lang = post.language === 'en' ? 'en' : 'es';
  const base = lang === 'en' ? '/news' : '/noticias';
  const paths = {
    en: { news: 'news', tech: 'tech', business: 'business', movie: 'business', series: 'tech', music: 'news' },
    es: { news: 'actualidad', tech: 'tecnologia', business: 'negocios', movie: 'negocios', series: 'tecnologia', music: 'actualidad' }
  };
  const categoryPath = paths[lang][post.category] || paths[lang].news;
  return `${base}/${categoryPath}/${post.slug}`;
}

const AllNews = () => {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const lang = language === 'en' ? 'en' : 'es';
  const [posts, setPosts] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);

  useEffect(() => {
    const pageUrl = `${window.location.origin}/all-news`;
    setPageOgMeta({
      title: lang === 'en'
        ? 'All news | Es tu mejor opción'
        : 'Todas las noticias | Es tu mejor opción',
      description: lang === 'en'
        ? 'Browse all articles from the Es tu mejor opción blog.'
        : 'Explora todos los artículos del blog de Es tu mejor opción.',
      url: pageUrl,
      type: 'website'
    });
  }, [lang]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const offset = (page - 1) * PAGE_SIZE;
    fetch(`${API_BASE}/api/blog/posts?language=${lang}&limit=${PAGE_SIZE}&offset=${offset}`, { credentials: 'include' })
      .then((r) => r.ok ? r.json() : { posts: [], total: 0 })
      .then((data) => {
        if (!cancelled) {
          setPosts(Array.isArray(data.posts) ? data.posts : []);
          setTotal(data.total != null ? data.total : 0);
        }
      })
      .catch(() => { if (!cancelled) { setPosts([]); setTotal(0); } })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [lang, page]);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div className="landing landing-theme-dark min-vh-100">
      <SiteHeader variant="marketing" />

      <section className="landing-news landing-news-section py-4 py-md-5 text-secondary site-main-with-header" style={{ minHeight: '60vh' }}>
        <Container>
          <div className="d-flex flex-wrap align-items-center justify-content-between gap-3 mb-4">
            <h1 className="landing-news-heading mb-0">{t('allNews.title', language)}</h1>
            <button
              type="button"
              className="landing-news-all-link border-0 bg-transparent"
              onClick={() => navigate('/')}
            >
              ← {t('allNews.backToHome', language)}
            </button>
          </div>

          {loading ? (
            <div className="text-center py-5">
              <Spinner animation="border" variant="light" />
              <p className="mt-3 opacity-75">{t('allNews.loading', language)}</p>
            </div>
          ) : posts.length === 0 ? (
            <p className="text-center opacity-75 py-5">{t('allNews.noPosts', language)}</p>
          ) : (
            <>
              <Row xs={1} md={2} lg={3} className="g-4">
                {posts.map((post) => (
                  <Col key={post.id}>
                    <Link to={getPostUrl(post)} className="text-decoration-none d-block h-100 landing-news-slide-link">
                      <Card className="h-100 border-0 checkout-shell-card shadow-none overflow-hidden">
                        {post.featured_image ? (
                          <div style={{ height: 200, overflow: 'hidden' }}>
                            <img
                              src={post.featured_image.startsWith('http') ? post.featured_image : API_BASE + post.featured_image}
                              alt=""
                              className="w-100 h-100 object-fit-cover"
                              style={{ objectFit: 'cover' }}
                            />
                          </div>
                        ) : (
                          <div className="bg-secondary d-flex align-items-center justify-content-center" style={{ height: 200 }}>
                            <span className="display-4 text-white opacity-50">📰</span>
                          </div>
                        )}
                        <Card.Body>
                          <Card.Title className="fw-bold small mb-2 line-clamp-2 text-white" style={{ minHeight: '2.5em' }}>
                            {post.title}
                          </Card.Title>
                          <p className="small mb-0 line-clamp-3 opacity-75">{post.excerpt || post.title}</p>
                          <span className="small mt-2 d-inline-block" style={{ color: 'var(--landing-accent)' }}>{t('allNews.readMore', language)} →</span>
                        </Card.Body>
                      </Card>
                    </Link>
                  </Col>
                ))}
              </Row>

              {totalPages > 1 && (
                <div className="d-flex justify-content-center mt-4 pt-4">
                  <Pagination>
                    <Pagination.Prev
                      disabled={page <= 1}
                      onClick={(e) => { e?.preventDefault?.(); setPage((p) => Math.max(1, p - 1)); }}
                    />
                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                      let pageNum;
                      if (totalPages <= 5) pageNum = i + 1;
                      else if (page <= 3) pageNum = i + 1;
                      else if (page >= totalPages - 2) pageNum = totalPages - 4 + i;
                      else pageNum = page - 2 + i;
                      return (
                        <Pagination.Item
                          key={pageNum}
                          active={pageNum === page}
                          onClick={(e) => { e?.preventDefault?.(); setPage(pageNum); }}
                        >
                          {pageNum}
                        </Pagination.Item>
                      );
                    })}
                    <Pagination.Next
                      disabled={page >= totalPages}
                      onClick={(e) => { e?.preventDefault?.(); setPage((p) => p + 1); }}
                    />
                  </Pagination>
                </div>
              )}
            </>
          )}
        </Container>
      </section>

      <footer className="landing-footer-v2 py-4 text-center border-top border-secondary">
        <Container>
          <button type="button" className="landing-footer-link border-0 bg-transparent w-auto mx-auto" onClick={() => navigate('/')}>
            ← {t('allNews.backToHome', language)}
          </button>
        </Container>
      </footer>
    </div>
  );
};

export default AllNews;
